import { ChangeEvent, ClipboardEvent, KeyboardEvent, WheelEvent } from "react";
import { SystemStyleObject } from "@mui/system";

// Hides the native spin buttons on a number field. They step by `step`
// (default 1), which is meaningless for a price or an area, and the arrow keys
// are blocked for the same reason (see numericInputProps). Typed as a plain
// style object (not SxProps) so it can be spread into a makeSxStyles entry.
export const numericFieldSx: SystemStyleObject = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

// Helpers for the `type="number"` inputs on the CRM forms.
//
// A number input reports `value === ""` for BOTH a cleared field and content it
// cannot parse ("12e", "--", "+", a lone "."), so treating "" as "cleared" let
// rejected input pass validation silently while it stayed visible in the field
// (QA on ESR-1566). `validity.badInput` tells the two apart: unparseable input
// becomes NaN, which every `z.number()` rule rejects, so the schema's
// `invalid_type_error` message renders instead of the field going quietly empty.
export const readNumericInput = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
): number | undefined => {
  if (event.target.validity.badInput) return NaN;
  return event.target.value === "" ? undefined : Number(event.target.value);
};

// A controlled input cannot display NaN — React would write the literal string
// "NaN" into the field — so the marker renders as an empty field and the
// schema's message appears beneath it until a valid number is entered.
export const numericInputValue = (value: number | undefined): number | "" =>
  value === undefined || Number.isNaN(value) ? "" : value;

// Numeric fields show their error once touched, but also when a value seeded
// from the API fails the schema: a record saved before these rules existed
// (fractional square footage, a difficulty outside 1–5) would otherwise leave
// Save disabled with nothing on screen explaining why. A blank field is valid
// (these are optional), so a fresh form still starts clean.
export const showNumericError = ({
  touched,
  value,
  error,
  focused,
  reportUntouched = true,
}: {
  touched: boolean | undefined;
  value: number | undefined;
  error: string | undefined;
  focused?: boolean;
  // Pass false where the form starts on a placeholder default that fails its own
  // rule — Service Duration begins at 0 with a ">= 1" rule, so reporting an
  // untouched value would put an error on a form nobody has filled in yet. The
  // default suits the optional CRM fields, where a value being present at all
  // means someone typed it or the API returned it.
  reportUntouched?: boolean;
}): boolean => {
  if (!error) return false;
  // Typing "1200.50" passes through "1200.", which the browser reports as
  // unparseable for one keystroke. Hold that message until the field is left,
  // so a valid decimal doesn't flash an error mid-entry. Errors on a value that
  // DID parse (negative, too many decimals, not a whole number) still show live.
  if (focused && Number.isNaN(value)) return false;
  return !!touched || (reportUntouched && value !== undefined);
};

export interface NumericInputAttributes {
  inputMode: "numeric" | "decimal";
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
  onWheel: (event: WheelEvent<HTMLInputElement>) => void;
}

// Spread into a TextField's `inputProps` (native input attributes) to keep
// non-numeric characters out of the field, whether typed or pasted. A number
// input rejects letters in Chrome but not in Firefox, and accepts "e"/"+"/"-"
// everywhere — none of which the CRM numbers (all positive, no exponents) need.
export const numericInputProps = ({
  allowDecimal = false,
  allowStepping = false,
}: {
  allowDecimal?: boolean;
  allowStepping?: boolean;
} = {}): NumericInputAttributes => {
  const allowedPaste = allowDecimal ? /^\d*(\.\d*)?$/ : /^\d*$/;

  return {
    inputMode: allowDecimal ? "decimal" : "numeric",
    onKeyDown: (event) => {
      // Up/Down step by `step` (default 1). That's meaningful on a small
      // bounded integer like Difficulty (min/max keep it in range), but on a
      // price or an area it silently turns 15.40 into 16.40 (or 16 — browsers
      // differ on whether the decimals survive), so it's off unless asked for.
      if (
        !allowStepping &&
        (event.key === "ArrowUp" || event.key === "ArrowDown")
      ) {
        event.preventDefault();
        return;
      }
      // Other named keys (Tab, Backspace, Home…) and shortcuts pass through;
      // of the single-character keys, only digits (and "." where decimals are
      // allowed) are accepted.
      if (
        event.key.length > 1 ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }
      const isDigit = event.key >= "0" && event.key <= "9";
      if (!isDigit && !(allowDecimal && event.key === ".")) {
        event.preventDefault();
      }
    },
    onPaste: (event) => {
      if (!allowedPaste.test(event.clipboardData.getData("text").trim())) {
        event.preventDefault();
      }
    },
    // Scrolling the page with the cursor over a focused number input otherwise
    // changes its value. Blurring (rather than preventDefault) because React
    // registers `wheel` as a passive root listener, where preventDefault is a
    // no-op. This holds even with `allowStepping`: the wheel fires while the
    // user is scrolling the page, with no intent to edit, unlike the arrow keys
    // and spin buttons, which need deliberate focus or a click.
    onWheel: (event) => {
      event.currentTarget.blur();
    },
  };
};
